/**
 * Confirm-before-close for a dirty editor, driving the shared ConfirmDialog
 * instead of window.confirm — a native dialog freezes the whole tab and looks
 * nothing like the app. Derived from the FO hook, decoupled from its context.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useCallback, useState } from 'react';

export interface DirtyCloseGuard {
  /** Close, or open the confirm when there are unsaved changes. */
  requestClose: () => void;
  confirming: boolean;
  discard: () => void;
  keepEditing: () => void;
}

export function useDirtyCloseGuard(
  onClose: () => void,
  dirty: boolean,
): DirtyCloseGuard {
  const [confirming, setConfirming] = useState(false);
  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirming(true);
      return;
    }
    onClose();
  }, [onClose, dirty]);
  const discard = useCallback(() => {
    setConfirming(false);
    onClose();
  }, [onClose]);
  const keepEditing = useCallback(() => setConfirming(false), []);
  return { requestClose, confirming, discard, keepEditing };
}
